import type { Range } from '@studio/js-utils/range';
import type { PTZ } from '@studio/models/ptz';
import { toPtz } from '@studio/models/ptz';
import type { DeepPartial } from '@tryiris-ai/common.protojs';
import type { FloatRange } from '@tryiris-ai/common.protojs/api/v1/device_settings';
import { isNil, toNumber } from '@tryiris-ai/js-types';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { PbPtzLike } from './types.js';

/**
 * Get the latest value from the specified values.
 *
 * @param value1 - First value.
 * @param value2 - Second value.
 * @returns Latest value.
 */
export const latestValue = <T extends { meta?: T_Maybe<{ lastModified?: T_Maybe<number> }> }>(
  value1?: T,
  value2?: T
): T_Maybe<T> =>
  (value1?.meta?.lastModified ?? -1) > (value2?.meta?.lastModified ?? -1) ? value1 : value2;

/**
 * Get the latest value from the specified values, preferring the control value if the delta is
 * within the specified range. If the delta is outside the range, the status value is returned.
 *
 * @param control - Control value.
 * @param status - Status value.
 * @param range - Range within which the control value is preferred.
 * @returns Latest value.
 */
export const latestValuePreferControlWithinRange = <
  T extends { meta?: T_Maybe<{ lastModified?: T_Maybe<number> }>; value?: T_Maybe<number> }
>(
  control?: T,
  status?: T,
  range: number = 0.05
): T_Maybe<T> => {
  const delta =
    control?.value !== undefined && status?.value !== undefined
      ? control.value - status.value
      : Number.POSITIVE_INFINITY;

  if (Math.abs(delta) <= range) {
    return control;
  }

  return latestValue(control, status);
};

/**
 * Convert a partial protobuf PTZ value to a JS PTZ value. Nil axes are defaulted to zero.
 *
 * @param pbPtz - Protobuf PTZ value.
 * @returns JS PTZ value.
 */
export const pbToJsPtz = (pbPtz: T_Maybe<DeepPartial<PbPtzLike>>): PTZ =>
  toPtz({
    pan: toNumber(pbPtz?.pan),
    tilt: toNumber(pbPtz?.tilt),
    zoom: toNumber(pbPtz?.zoom)
  });

/**
 * Preset/status pose conversion without clamping. Joystick intents keep
 * {@link pbToJsPtz}. Values past [-1, 1] are legal when DR metadata is wrong.
 */
export const pbToJsPtzPosition = (pbPtz: T_Maybe<DeepPartial<PbPtzLike>>): PTZ =>
  ({
    pan: toNumber(pbPtz?.pan) ?? 0,
    tilt: toNumber(pbPtz?.tilt) ?? 0,
    zoom: toNumber(pbPtz?.zoom) ?? 0
  }) as PTZ;

/** @internal */
export const pbToJsRange = (pbRange: T_Maybe<DeepPartial<FloatRange>>): T_Maybe<Range> => {
  if (!pbRange) {
    return undefined;
  }

  const min = toNumber(pbRange.min);
  const max = toNumber(pbRange.max);

  if (isNil(min) || isNil(max)) {
    return undefined;
  }

  return {
    max,
    min
  };
};
